import { Button, TextInput } from '@mcgames/ui';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './Games.module.css';
import type { StepDraft } from './install-draft.ts';

export type StepEditorProps = {
  step: StepDraft;
  /** 1-based position in the list. */
  position: number;
  count: number;
  onChange: (step: StepDraft) => void;
  onMove: (direction: -1 | 1) => void;
  onRemove: () => void;
};

/** One install step: its form, which depends on the step type, and the move and remove buttons. */
export function StepEditor({ step, position, count, onChange, onMove, onRemove }: StepEditorProps) {
  const { t } = useTranslation('admin');
  const ids = useId();

  function field(key: 'path' | 'from' | 'to', label: string, hint: string) {
    const hintId = `${ids}-${key}-hint`;
    return (
      <>
        <TextInput
          label={label}
          value={step[key]}
          required
          aria-describedby={hintId}
          onChange={(event) => onChange({ ...step, [key]: event.target.value })}
        />
        <span id={hintId} className={styles['hint']}>
          {hint}
        </span>
      </>
    );
  }

  function argsField() {
    const id = `${ids}-args`;
    return (
      <div className={styles['fieldset']}>
        <label className={styles['fieldLabel']} htmlFor={id}>
          {t('games.install.fields.args')}
        </label>
        <textarea
          id={id}
          className={styles['textarea']}
          rows={4}
          spellCheck={false}
          value={step.args}
          onChange={(event) => onChange({ ...step, args: event.target.value })}
        />
      </div>
    );
  }

  const extractRelative = t('games.install.fields.extractRelative');
  const installRelative = t('games.install.fields.installRelative');
  const timeoutHintId = `${ids}-timeout-hint`;

  return (
    <fieldset className={styles['step']}>
      <legend className={styles['stepLegend']}>
        {t('games.install.stepTitle', {
          position,
          kind: t(`games.install.kinds.${step.type}`),
        })}
      </legend>
      <p className={styles['hint']}>{t(`games.install.help.${step.type}`)}</p>
      {(step.type === 'extract_iso' || step.type === 'run_installer' || step.type === 'redist') &&
        field('path', t('games.install.fields.path'), extractRelative)}
      {step.type === 'set_exe' && field('path', t('games.install.fields.path'), installRelative)}
      {step.type === 'copy' && (
        <>
          {field('from', t('games.install.fields.from'), extractRelative)}
          {field('to', t('games.install.fields.to'), installRelative)}
        </>
      )}
      {(step.type === 'run_installer' || step.type === 'redist') && argsField()}
      {step.type === 'run_installer' && (
        <>
          <div className={styles['narrow']}>
            <TextInput
              label={t('games.install.fields.timeout')}
              type="number"
              min={1}
              step={1}
              value={step.timeoutMinutes}
              aria-describedby={timeoutHintId}
              onChange={(event) => onChange({ ...step, timeoutMinutes: event.target.value })}
            />
          </div>
          <span id={timeoutHintId} className={styles['hint']}>
            {t('games.install.fields.timeoutHint')}
          </span>
        </>
      )}
      <div className={styles['row']}>
        <Button size="sm" variant="secondary" disabled={position === 1} onClick={() => onMove(-1)}>
          {t('games.install.moveUp', { position })}
        </Button>
        <Button
          size="sm"
          variant="secondary"
          disabled={position === count}
          onClick={() => onMove(1)}
        >
          {t('games.install.moveDown', { position })}
        </Button>
        <Button size="sm" variant="danger" onClick={onRemove}>
          {t('games.install.remove', { position })}
        </Button>
      </div>
    </fieldset>
  );
}
